import { Component, ElementRef, ViewChild, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';

import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Store } from '@ngxs/store';
import { UserState } from 'app/state/user/state';
import { AccountState } from 'app/state/account/state';
import { TsSnackbarService } from 'tsui';
import { Account } from 'app/core/models';
import { AggregatedTasksService } from 'app/modules/aggregated-tasks/aggregated-tasks.service';
import { RelatedToType } from 'app/shared/enums';
import { Task, TaskStatus } from 'app/shared/modules/legacy-tasks/tasks.types';
import { FormFieldComponent } from '../form-field.types';
import { ProjectAddContactComponent } from 'app/modules/projects/modals/project-add-contact/project-add-contact.component';
import { ProjectAddCompanyComponent } from 'app/modules/projects/modals/project-add-company/project-add-company.component';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { FileResource, FolderResource } from 'app/shared/modules/folders/folders.types';
import { FoldersTabViewFileComponent } from 'app/shared/modules/folders/view-file/view-file.component';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, Property } from 'app/modules/projects/projects.types';
import { AddLinkToCustomObjectValueDialog } from '@shared/components/add-link-to-custom-object-value/add-link-to-custom-object-value.component';
import { ObjectsManagerService } from 'app/modules/settings/objects-manager/services/objects-manager.service';
import { CustomObject, CustomObjectValue } from 'app/shared/modules/custom-objects/custom-objects.types';
import { FeatureFlagService } from 'app/core/services/feature-flag.service';
import { ConfirmService } from '../../widgets/dialog/confirm/confirm.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { AccountFoldersService } from 'app/modules/folders/folders.service';
import { Folders } from '@shared/modules/folders/state/actions';
import { Company } from 'app/modules/companies/companies.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import { FormFieldUtils } from '@shared/Utils/form-field.utils';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlag } from '@core/enums';
const STANDARD_RELATED_TO_TYPES = [
  RelatedToType.Project,
  RelatedToType.Property,
  RelatedToType.Company,
  RelatedToType.Contact,
  RelatedToType.User,
  RelatedToType.FileResource,
  RelatedToType.Task,
  RelatedToType.Engagement,
];

@Component({
  templateUrl: './contact-form-field.component.html',
  styleUrls: ['./contact-form-field.component.scss'],
})
export class ContactFormFieldComponent extends FormFieldComponent implements OnInit {
  @ViewChild('inputControl') inputControl: ElementRef;

  editing = false;
  value: any;
  customObjType: string;
  workflowStates: TaskStatus[];

  readonly NONE_VALUE = '__NONE__';
  previousSelections: any[] = [];
  isNewModel = false;
  options: any[];
  filteredOptions: any[];
  account: Account;

  fileResource: FileResource;
  fileResourceMap = {};
  uploadingFile = false;

  isCustomObjectLookup = false;
  customObjectId: number;
  customObjects = this.store.selectSnapshot(ObjectsManagerState.get);
  customObject: CustomObject;
  multipleUserIds: number[];
  private isNewCrmOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_CRM));

  private _project: Project;
  private _originalValue: any;

  get displayValue() {
    return 'REDACTED';
  }

  private get isRelatedToUser(): boolean {
    return this.fieldDef?.meta?.record_type === RelatedToType.User;
  }

  get isMultipleField(): boolean {
    return this.fieldDef?.meta?.multi_select;
  }

  get isReadOnly(): boolean {
    return this.fieldDef?.meta?.readonly || this.readonly;
  }

  get hasImageRecordFormat(): boolean {
    return this.fieldDef?.meta?.allowed_record_formats?.includes('image');
  }

  get imageRecordFormats(): string[] {
    return FormFieldUtils.imageRecordFormats;
  }

  private get isRelatedToTask(): boolean {
    return this.fieldDef?.related_to_type === RelatedToType.Task;
  }

  private get isRelatedToCO(): boolean {
    return !STANDARD_RELATED_TO_TYPES.includes(this.fieldDef.related_to_type);
  }

  get acceptAttribute(): string {
    return FormFieldUtils.getInputAcceptFileAttribute(
      (this.fieldDef?.meta?.allowed_record_formats as FormFieldUtils.AllowedRecordFormats[]) || ['any'],
    );
  }

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _matDialog: MatDialog,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _aggregatedTasksService: AggregatedTasksService,
    private readonly _accountFoldersService: AccountFoldersService,
    private readonly foldersTabService: FoldersTabService,
    private readonly projectsService: ProjectsService,
    private readonly snackBar: TsSnackbarService,
    private readonly objectsManagerService: ObjectsManagerService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly confirmService: ConfirmService,
    private readonly store: Store,
    private readonly destroyRef: DestroyRef,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
    this._aggregatedTasksService.accountId = this.account.id;
    this.foldersTabService.accountId = this.account.id;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.value = this.model[this.fieldDef.name];
    if (this.isMultipleField && this.isRelatedToUser) {
      this.multipleUserIds = this.value?.map((val: string) => +val.split('|')[0]);
    }
    this.parseContactType();
    this._originalValue = this.value;

    this.isNewModel = this.model?.id == null;
    this.customObjType = this.fieldDef?.meta?.record_type;
    this.customObject = this.data?.customObject;
    this.isCustomObjectLookup =
      (this.fieldDef?.name?.startsWith('cf_linked_') || this.isFieldMetaRelatedToCO()) ?? false;

    this.handleCustomObjType();

    this.objectsManagerService
      .get(Number(this.account.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customObjects) => {
        this.handleCustomObjectId(customObjects);
      });
  }

  private isFieldMetaRelatedToCO(): boolean {
    return !STANDARD_RELATED_TO_TYPES.includes(
      this.fieldDef?.meta?.record_type || this.fieldDef?.meta?.related_to_type,
    );
  }

  canShowAssociationButton(): boolean {
    return ((!this.value || this.value?.length === 0) && this.isMultipleField) || !this.isReadOnly;
  }

  private handleCustomObjType(): void {
    if (this.fieldDef?.meta?.related_to_type === RelatedToType.Task) {
      this.customObjType = RelatedToType.Task;
    }

    switch (this.customObjType) {
      case RelatedToType.User:
        this.options = this.store.selectSnapshot(UserState.getUsers);
        this.filteredOptions = this.options;
        break;

      case RelatedToType.FileResource:
        if (this.fieldDef.name in this.model && !this.isMultipleField) {
          this.setFolderSettings();

          const fieldValue = this.model[this.fieldDef.name]?.includes('|')
            ? this.model[this.fieldDef.name].split('|')[0]
            : this.model[this.fieldDef.name];

          if (fieldValue) {
            this.store
              .dispatch(new Folders.GetFileById(+fieldValue))
              .pipe(untilComponentDestroyed(this))
              .subscribe(() => {
                const fileResource: FileResource = this.store.selectSnapshot((state) => state.folderState.selectedFile);
                this.fileResource = fileResource;
              });
          }
        }

        break;

      case RelatedToType.Task:
        if (this.value) this.value = JSON.parse(String(this.value));
        this._aggregatedTasksService
          .getTaskStatuses()
          .pipe(untilComponentDestroyed(this))
          .subscribe((taskStatuses) => {
            this.workflowStates = taskStatuses;
          });
        break;

      case RelatedToType.Project:
        this.getProjectData();
        break;
    }
  }

  goToRecord(item?: any) {
    if (this.customObjType === RelatedToType.FileResource) {
      this.viewFileFromFileResourceCustomObject(item);
      return;
    }

    if (this.isDisabled) {
      return;
    }

    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    let vals = this.getValues();

    if (item) {
      vals = item.split('|');
    }

    const id = vals[0];

    const redirectConfig = this.store.selectSnapshot(ObjectsManagerState.getRedirectConfig)[this.customObjType];

    if ([RelatedToType.Contact, RelatedToType.Company].includes(this.customObjType as RelatedToType)) {
      if (this.isNewCrmOn) {
        this._router.navigate(['/space/void'], { queryParams: { ...redirectConfig.getQueryParams(id) } });
      } else {
        const obj = this.store.selectSnapshot(ObjectsManagerState.get).find((x) => x.name === this.customObjType);
        this._router.navigate(['/', obj.apiResponseConfig.api_path, id]);
      }
    } else if (this.customObjType == RelatedToType.Task) {
      this._router.navigate([
        '/projects',
        this.model.pipeline_id,
        this.value.project_id,
        'tasks',
        this.value.task_list_id,
        this.value.id,
      ]);
    } else if (this.customObjType == RelatedToType.Property) {
      this._router.navigateByUrl(`/space/void?return=/space/properties/0/${id}`);
    } else if (this.isCustomObjectLookup) {
      if (this.customObjType == RelatedToType.Property) {
        this._router.navigateByUrl(`/space/void?return=/space/properties/0/${id}`);
      } else if (this.customObjType == RelatedToType.Project) {
        this._router.navigate(['/projects', this._project?.pipeline_id, id]);
      } else {
        this._router.navigate(['/space/void'], { queryParams: { ...redirectConfig.getQueryParams(id) } });
      }
    } else {
      this.foldersTabService
        .getFile(this.model.root_folder_resource_id, id)
        .pipe(untilComponentDestroyed(this))
        .subscribe((fileResource) => {
          this.viewFile(fileResource);
        });
    }
  }

  updateTask(task: Task): void {
    this._aggregatedTasksService.simpleUpdateTask(task).pipe(untilComponentDestroyed(this)).subscribe();
  }

  private viewFileFromFileResourceCustomObject(item?: any): void {
    if (this.fileResourceMap[item]) {
      this.viewFile(this.fileResourceMap[item]);
      return;
    } else {
      const fieldValue = item?.includes('|') ? item.split('|')[0] : item;

      if (fieldValue) {
        this.store
          .dispatch(new Folders.GetFileById(+fieldValue))
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            const fileResource: FileResource = this.store.selectSnapshot((state) => state.folderState.selectedFile);
            this.fileResourceMap[item] = fileResource;
            this.viewFile(fileResource);
          });
      }
    }
  }

  public openTypeRelatedModal(): void {
    const data = {
      panelClass: 'mailbox-compose-dialog',
      data: {
        byPass: true,
        model: this.model,
        multi_select: this.fieldDef.meta.multi_select,
        related_to_type: this.fieldDef.related_to_type,
      },
    };

    const customObjectData = {
      width: '40vw',
      height: 'auto',
      data: {
        customObjectType: this.customObjType,
        customObjectId: this.customObjectId,
        related_to_type: this.fieldDef.related_to_type,
        model: this.model,
        canAddNewRecord:
          this.isCustomObjectLookup &&
          !STANDARD_RELATED_TO_TYPES.includes(this.fieldDef.meta?.record_type as RelatedToType),
      },
    };

    let dialogRef: MatDialogRef<any>;

    if (this.customObjType == RelatedToType.Contact) {
      dialogRef = this._matDialog.open(ProjectAddContactComponent, data);
    } else if (this.customObjType == RelatedToType.Company) {
      dialogRef = this._matDialog.open(ProjectAddCompanyComponent, data);
    } else if (
      this.isCustomObjectLookup ||
      this.customObjType == RelatedToType.Project ||
      this.customObjType == RelatedToType.Property
    ) {
      dialogRef = this._matDialog.open(AddLinkToCustomObjectValueDialog, customObjectData);
    }

    dialogRef
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result) => !!result),
      )
      .subscribe((result: { model: any; addedExistedRecord: boolean }) => {
        const fieldValue = this.setFieldValueAfterDialogClose(result);
        this.value = fieldValue;
        this.onSave(this.value);

        // If this form is being used before a model has been saved i.e. the create form.
        if (this.isNewModel) {
          // JERRY FOR OPENAREA
          if (
            (this.account?.subdomain == 'openarea' || this.account?.subdomain == 'area') &&
            this.customObjType == RelatedToType.Company
          ) {
            this.update.emit({ ['title']: `${result?.model?.name} Deal` });
          }
        }
      });
  }

  removeModelField(event: Event): void {
    event.stopPropagation();

    if (this.customObjType === RelatedToType.FileResource && !this.fileResource) {
      this.errorSnackBar();
      return;
    }

    if (this.isNewModel) {
      this.updateModel(null);
      this.update.emit({ [this.fieldDef.name]: null });
      this._changeDetectorRef.markForCheck();
      this.value = null;
    } else {
      this.confirmService
        .openConfirmationModal()
        .pipe(filter((result: boolean) => result))
        .subscribe(() => {
          this.updateModel(null);
          this.update.emit({ [this.fieldDef.name]: null });
          this._changeDetectorRef.markForCheck();
          this.value = null;
          this._originalValue = null;
        });
    }
  }

  updateChangedModel(model: any): void {
    this.model = model;
    this.parseContactType();
    this._originalValue = this.model[this.fieldDef.name];
  }

  private setFieldValueAfterDialogClose(result: { model: any; addedExistedRecord: boolean }): unknown {
    const { model, addedExistedRecord } = result;
    let fieldValue: unknown;

    // If this is a custom object lookup, we need to get the field value from the custom object's search field
    if (
      this.isCustomObjectLookup &&
      !STANDARD_RELATED_TO_TYPES.includes(this.fieldDef.meta?.record_type as RelatedToType)
    ) {
      // Find the custom object context using the customObjectId
      const contextCustomObject = this.customObjects.find((x) => x.id === Number(this.customObjectId));
      // Get the name of the search field that was used to find this custom object value
      const searchFieldName = contextCustomObject.fields.find(
        (x) => x.id === contextCustomObject.search_custom_field_id,
      )?.name;

      // Get the value of the search field from the result's model data if it's a new record, otherwise use the name field
      // if the search field is not found, send an empty string since we don't know what field to display.
      fieldValue = (addedExistedRecord ? model.name : model[searchFieldName]) ?? 'Search field missing';
    } else {
      // For standard lookups (non-custom objects), use the model's name as the field value
      fieldValue = model.name;
    }

    const formattedValue = fieldValue === '' ? model.id : `${model.id}|${fieldValue}`;

    if (this.isMultipleField) {
      const value = this.value ? [...this.value, formattedValue] : [formattedValue];
      return [...new Set(value)];
    } else {
      return formattedValue;
    }
  }

  private getValues() {
    if (this.isNewModel) {
      return [this.model[this.fieldDef.name], this.value];
    } else {
      if (this.model[this.fieldDef.name] && typeof this.model[this.fieldDef.name] === 'string')
        return this.model[this.fieldDef.name].split('|');
      else return null;
    }
  }

  private parseContactType(): void {
    const customObjType = this.fieldDef?.meta?.record_type;
    const values = this.getValues();

    if (this.value && values && values.length == 2) {
      if (customObjType == RelatedToType.User) {
        this.value = values[0];
        this.value = parseInt(this.value);
      } else {
        this.value = values[1];
      }
    }
  }

  removeItem(item: string): void {
    this.value = this.value.filter((val) => val !== item);
    this.update.emit({ [this.fieldDef.name]: this.value });
    this.updateModel(this.value);
    this._changeDetectorRef.markForCheck();
  }

  private onSave(value: any, pushNewValue = true): void {
    if (this._originalValue != value) {
      if (this.isMultipleField && pushNewValue) {
        let newIds = this._originalValue?.map((val: string) => +val?.split('|')[0]) || [];

        if (
          this.customObjType === RelatedToType.FileResource ||
          this.customObjType === RelatedToType.Company ||
          this.customObjType === RelatedToType.Contact
        ) {
          newIds = this._originalValue;
        }

        if (newIds) {
          value = (value instanceof Array ? [...newIds, ...value] : [...newIds, value]).filter(
            (x) => !(typeof x === 'number'),
          );
        } else {
          value = value instanceof Array ? value : [value];
        }

        value = [...new Set(value)];
      }
      this.update.emit({ [this.fieldDef.name]: value });
      this.updateModel(value);
      this._changeDetectorRef.markForCheck();
    }
  }

  private updateModel(value: any): void {
    this.model[this.fieldDef.name] = value;
    this._originalValue = this.value;
  }

  filterQueryParams(): string | undefined {
    const value = this.getValues();

    if (!value) return undefined;

    let fjson = [[this.fieldDef.name, 'in', [[parseInt(value[0]), value[1]]]]];
    let queryFilter = {
      fjson: JSON.stringify(fjson),
      q: '',
    };

    return JSON.stringify(queryFilter);
  }

  edit(): void {
    if (!this.editing && !this.isReadOnly) {
      this.editing = true;
    }
  }

  onBlur($event: Event): void {
    this.editing = false;
  }

  compareWithNull(a: any, b: any): boolean {
    return a === b;
  }

  onSelectionChange($event: MatSelectChange): void {
    let value = $event.value;
    if (this.isMultipleField) {
      if (Array.isArray(value)) {
        if (value.includes(this.NONE_VALUE) && !this.previousSelections.includes(this.NONE_VALUE)) {
          value = [this.NONE_VALUE];
        } else {
          value = value.filter((v) => v !== this.NONE_VALUE);
        }
      }
      // Update the bound value so the UI reflects the change
      if (this.isRelatedToUser) {
        this.multipleUserIds = value;
      } else {
        this.value = value;
      }
      this._changeDetectorRef.detectChanges();
    }
    //If the value if you have NONE_VALUE, then we need to save [null]
    const _updatedValue = value?.includes?.(this.NONE_VALUE) ? [null] : value;
    this.previousSelections = value;
    this.onSave(_updatedValue, false);
  }

  isFilterable(): boolean {
    return this.fieldDef.related_to_type === RelatedToType.Property;
  }

  hasNoRecordType(): boolean {
    return !this.fieldDef?.meta?.record_type;
  }

  private setFolderSettings(): void {
    switch (this.data?.relatedToType) {
      case RelatedToType.Project:
        this.foldersTabService.moduleId = (this.data.project as Project).id;
        this.foldersTabService.moduleBaseUrl = 'projects';
        break;
      case RelatedToType.Property:
        this.foldersTabService.moduleId = (this.data.property as Property).id.toString();
        this.foldersTabService.moduleBaseUrl = 'properties';
        break;
      case RelatedToType.Contact:
        this.foldersTabService.moduleId = (this.data.contact as Contact).id;
        this.foldersTabService.moduleBaseUrl = 'contacts';
        break;
      case RelatedToType.Company:
        this.foldersTabService.moduleId = (this.data.company as Company).id;
        this.foldersTabService.moduleBaseUrl = 'companies';
        break;
      case RelatedToType.Task:
        this.foldersTabService.moduleId = (this.model as Task).id.toString();
        this.foldersTabService.moduleBaseUrl = 'tasks';
        break;
      case RelatedToType.CustomObjectValue:
        this.foldersTabService.moduleId = (this.model as CustomObjectValue).id.toString();
        this.foldersTabService.moduleBaseUrl = RelatedToType.CustomObjectValue;
        break;
    }
  }

  private handleTaskFile(file: File): {
    createFileObservable$: Observable<FileResource>;
    uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;
  } {
    const fileResource: FileResource = {
      related_to_type: RelatedToType.Task,
      related_to_id: this.model.id,
      name: file.name,
    };

    const createFileObservable$ = this._accountFoldersService.getFolder().pipe(
      switchMap((folder: FolderResource) => {
        fileResource.folder_resource_id = folder.id;
        return this._accountFoldersService.createFile(folder.id, fileResource);
      }),
    );

    const uploadDocumentObservable = (fileResource: FileResource) =>
      this._accountFoldersService.uploadDocument(fileResource.folder_resource_id, fileResource.id, file);

    return { createFileObservable$, uploadDocumentObservable };
  }

  private handleCustomObjectFile(file: File): {
    createFileObservable$: Observable<FileResource>;
    uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;
  } {
    const folderId = this.model.id;
    const fileResource: FileResource = {
      folder_resource_id: folderId,
      name: file.name,
    };

    const createFileObservable$ = this.foldersTabService.getFolder(null, this.customObject.id).pipe(
      switchMap((folder: FolderResource) => {
        fileResource.folder_resource_id = folder.id;
        return this.foldersTabService.createFile(folder.id, fileResource, this.customObject.id);
      }),
    );

    const uploadDocumentObservable = (fileResource: FileResource) =>
      this.foldersTabService.uploadDocument(
        fileResource.folder_resource_id,
        fileResource.id,
        file,
        this.customObject.id,
      );

    return { createFileObservable$, uploadDocumentObservable };
  }

  private handleDefaultFile(file: File): {
    createFileObservable$: Observable<FileResource>;
    uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;
  } {
    const folderId = this.model?.root_folder_resource_id;
    const fileResource: FileResource = {
      folder_resource_id: folderId,
      name: file.name,
    };

    const createFileObservable$ = this.foldersTabService.createFile(folderId, fileResource);

    const uploadDocumentObservable = (fileResource: FileResource) =>
      this.foldersTabService.uploadDocument(folderId, fileResource.id, file);

    return { createFileObservable$, uploadDocumentObservable };
  }

  onFileChanged(event: InputEvent): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.uploadingFile = true;

    let createFileObservable$: Observable<FileResource>;
    let uploadDocumentObservable: (fileResource: FileResource) => Observable<FileResource>;

    if (this.isRelatedToTask) {
      ({ createFileObservable$, uploadDocumentObservable } = this.handleTaskFile(file));
    } else if (this.isRelatedToCO) {
      ({ createFileObservable$, uploadDocumentObservable } = this.handleCustomObjectFile(file));
    } else {
      ({ createFileObservable$, uploadDocumentObservable } = this.handleDefaultFile(file));
    }

    createFileObservable$
      .pipe(
        untilComponentDestroyed(this),
        switchMap(uploadDocumentObservable),
        catchError((err) => {
          this.snackBar.open('Unable to upload attachment at this moment. Please try again later.', 'error');
          return throwError(() => err);
        }),
        finalize(() => {
          this.uploadingFile = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe((fileResource) => {
        this.fileResource = fileResource;

        if (this.isMultipleField) {
          const value = this.value
            ? [...this.value, `${fileResource.id}|${fileResource.name}`]
            : [`${fileResource.id}|${fileResource.name}`];
          this.value = [...new Set(value)];
        } else {
          this.value = `${fileResource.id}|${fileResource.name}`;
        }

        this.onSave(this.value);
        this.snackBar.open('Successfully uploaded attachment', 'success');
      });
  }

  viewFile(fileResource: FileResource | FileResource[]): void {
    if (!this.value && this.readonly) {
      return;
    }

    if (!fileResource) {
      this.errorSnackBar();
      return;
    }

    fileResource = Array.isArray(fileResource) ? fileResource[0] : fileResource;

    this._matDialog.open(FoldersTabViewFileComponent, {
      panelClass: 'mailbox-compose-dialog',
      minWidth: '100vw',
      height: '100vh',
      data: { fileResource },
    });
  }

  downloadFile(file: FileResource): void {
    if (!file) {
      this.errorSnackBar();
      return;
    }

    window.open(file.document_url, '_blank');
  }

  private errorSnackBar(message: string = 'File not found'): void {
    this.snackBar.open(message, 'error', 'Close');
  }

  private getProjectData(): void {
    const vals = this.getValues();

    if (!vals) {
      return;
    }

    if (vals.filter((x) => !x).length > 0) {
      return;
    }

    this.projectsService
      .getProjectById(vals[0])
      .pipe(untilComponentDestroyed(this))
      .subscribe((project: Project) => {
        this._project = project;
      });
  }

  private handleCustomObjectId(customObjects: CustomObject[]): void {
    if (customObjects?.length) {
      this.customObjectId = customObjects.find(
        (customObject) => customObject.name?.toLowerCase() === this.customObjType?.toLowerCase(),
      )?.id;
    }
  }
}
